<template>
  <div
    :class="componentClass"
    :style="componentStyle"
  >
    <owa-tag
      v-if="shouldShowSaleTag"
      :background="`var(--fa-${planColor})`"
      color="var(--fa-navy)"
      label="On Sale!"
      position="top"
      size="size-sm"
    />

    <owa-tag
      v-else-if="isExpiredActivePlan && !isInModalContext"
      theme="gray inverted"
      label="Your Expired Plan"
      position="top"
      size="size-sm"
    />

    <span
      v-else-if="isCurrentPlan && !isInModalContext"
      class="tag size-sm"
      style="
        --tag-background: var(--current-plan-color, var(--fa-blue));
        --tag-color: var(--fa-navy);
        --tag-border-color: var(--current-plan-color, var(--fa-blue));
        --tag-border-width: 0;
      "
      >Your Plan</span
    >

    <owa-tag
      v-else-if="plan.mostPopular && !shouldShowSaleTag && !isDowngrade && !(isInModalContext && isSinglePlanModal) && !suppressFeatured"
      theme="yellow"
      label="Most Popular"
      position="top"
      size="size-sm"
    />

    <h2 class="margin-bottom-4xs">
      <span
        class="margin-right-4xs"
        :style="`color: var(--fa-${planColor}); display: inline-block; padding-bottom: var(--spacing-2xs)`"
      >
        <font-awesome-icon
          :icon="$v7[planIconStyle][icon]"
          size="lg"
          class="decorative fa-width-auto"
        />
      </span>
      <span class="size-md tablet:size-sm laptop:size-md tablet:display-block laptop:display-inline"
        ><span v-if="shortName">{{ shortName }}</span
        ><span v-else>{{ plan.name }}</span
        ><span
          v-if="isShowingNameTag"
          class="tag margin-left-sm"
          :style="`position: relative; transform: none; top: auto; left: auto; font-size: 0.75rem; vertical-align: middle; --tag-background: var(--white); --tag-color: var(--fa-dk-${planColor}); --tag-border-color: var(--fa-${planColor}); --tag-border-width: 2px; --tag-border-style: solid; cursor: default`"
          :aria-label="nameTagDescription"
          data-balloon-pos="up"
          data-balloon-length="medium"
          >{{ nameTagLabel }}</span
        ></span
      >
    </h2>

    <div>
      <template v-if="isInModalContext">
        <h3 class="sr-only">About this plan</h3>
        <p
          v-if="planDescriptionModal"
          class="margin-bottom-0 size-md"
          style="color: var(--fa-md-gravy)"
        >
          {{ planDescriptionModal }}
        </p>
      </template>

      <template v-else>
        <h3 class="sr-only">About This Plan</h3>

        <p
          v-if="planTagline"
          class="margin-bottom-0 size-lg"
          style="color: var(--fa-md-gravy)"
        >
          {{ planTagline }}
        </p>
        <p
          v-else
          class="margin-bottom-0"
        >
          {{ plan.description }}
        </p>
      </template>
    </div>

    <div
      class="margin-top-xl"
      style="display: flex; flex-direction: column; flex: 1"
    >
      <p
        class="strong margin-top-4xs margin-bottom-4xs"
        style="line-height: 1"
      >
        <template v-if="isShowingDiscount && currentDollars !== originalMonthlyDollars">
          <span class="sr-only">Regular price:</span>
          <span
            class="size-4xl tablet:size-3xl laptop:size-4xl margin-right-0"
            style="color: var(--fa-gravy)"
            ><s
              >{{ originalMonthlyDollars }}<span v-if="originalMonthlyCents">.{{ originalMonthlyCents }}</span></s
            ></span
          >
          <span class="sr-only">Current price:</span>
        </template>
        <span class="size-4xl tablet:size-3xl laptop:size-4xl"
          >{{ currentDollars }}<span v-if="currentCents">.{{ currentCents }}</span></span
        >
        <span
          class="size-lg margin-left-0"
          style="color: var(--fa-md-gravy)"
          >/mo</span
        >
      </p>

      <p
        class="size-md margin-top-0 margin-bottom-2xl"
        style="color: var(--fa-md-gravy)"
      >
        <template v-if="!isMonthlyCycle">
          Billed annually.
          <span
            v-if="discountLine"
            :style="`color: ${savingsTextColor}`"
          >{{ discountLine }}</span>
          <span
            v-else-if="annualSavingsLine"
            aria-label="Compared to monthly billing"
            data-balloon-pos="down"
            :style="`color: ${savingsTextColor}; cursor: default; white-space: nowrap`"
          >{{ annualSavingsLine }}</span>
        </template>
        <span
          v-else-if="discountLine"
          :style="`color: ${savingsTextColor}`"
        >{{ discountLine }}</span>
        <template v-else>Billed monthly</template>
      </p>

      <!-- margin-top:auto pins the action to the card bottom, so paired
           modal cards with different price-line heights keep aligned CTAs. -->
      <div style="margin-top: auto">
        <!-- Pending scheduled change to this plan. Precedes every other
           branch so the card the customer already committed to shows the
           management path instead of an actionable upgrade/downgrade CTA
           that would either no-op or compound the pending change. -->
      <site-link
        v-if="hasPendingChangeToThisPlan"
        :to="{ name: 'account.billing' }"
        class="button flat with-border size-lg block margin-y-0"
        :aria-label="pendingChangeScheduledTooltip"
        :data-balloon="pendingChangeScheduledTooltip"
        data-balloon-pos="up"
        data-balloon-length="large"
      >
        Plan Change Scheduled
      </site-link>

      <!-- Border keeps the dimensions equal to the .with-border buttons
           on sibling cards so cycle-toggle flips don't shift the layout. -->
      <span
        v-else-if="isCurrentPlan && !isExpiredPlan"
        :class="`button flat size-lg block margin-y-0 ${planColor} inverted`"
        style="pointer-events: none; border: 2px solid var(--fa-navy)"
        >Your Plan!</span
      >

      <!-- FR-021: Pro+ access loss is non-self-serve; surface a contact
           path instead of a dead disabled button. -->
      <a
        v-else-if="wouldLoseProPlusAccess"
        :href="proPlusContactMailto"
        class="button flat with-border size-lg block margin-y-0"
        :aria-label="proPlusBlockTooltip"
        :data-balloon="proPlusBlockTooltip"
        data-balloon-pos="up"
        data-balloon-length="large"
      >
        Contact Us
      </a>

      <!-- Annual → monthly transitions are gated pending downstream
           cycle-switch work. Same "Contact us" treatment as the Pro+
           gate; precedes the cycle-swap branch so a Solo-annual customer
           toggling to monthly view on /plans lands on the contact path
           instead of routing into /plans/change/solo-monthly. -->
      <a
        v-else-if="wouldRequireAnnualToMonthlySwitch"
        :href="annualToMonthlyContactMailto"
        class="button flat with-border size-lg block margin-y-0"
        :aria-label="annualToMonthlyBlockTooltip"
        :data-balloon="annualToMonthlyBlockTooltip"
        data-balloon-pos="up"
        data-balloon-length="large"
      >
        Contact Us
      </a>

      <!-- Must precede the isDowngrade branch: a cycle swap to monthly
           classifies as a downgrade by tier-direction rules but should
           read as "Switch to monthly" rather than "Switch to Solo" when
           the customer's already on Solo. -->
      <!-- Flat on the plans page (a secondary action); solid in a modal
           (the primary one). -->
      <site-link
        v-else-if="isSameTierCycleSwap"
        :class="isInModalContext ? `button size-lg block margin-y-0 ${planColor}` : `button flat with-border size-lg block margin-y-0`"
        :to="planLinkArguments(plan)"
        @click="trackThen($event, isInModalContext, plan)"
        >Switch to {{ planCycleWord }}</site-link
      >

      <site-link
        v-else-if="isDowngrade && !isInModalContext"
        :class="`button flat with-border size-lg block margin-y-0`"
        :to="planLinkArguments(plan)"
        @click="trackThen($event, isInModalContext, plan)"
      >
        Switch to {{ plan.name }}
      </site-link>

      <site-link
        v-else
        :class="`button size-lg block margin-bottom-0 ${planColor}`"
        :style="planColor === 'gravy' ? '--button-background: var(--white); --button-hover-background: var(--fa-lt-gravy)' : ''"
        :to="planLinkArguments(plan)"
        @click="trackThen($event, isInModalContext, plan)"
      >
        <template v-if="isInModalContext && hasActivePaidSubscription">Switch to {{ plan.name }}</template>
        <template v-else-if="hasActivePaidSubscription">Upgrade to {{ plan.name }}</template>
        <template v-else>Get {{ plan.name }}</template>
      </site-link>
      </div>
    </div>

    <div
      v-if="planFeatures"
      class="margin-top-3xl"
      style="flex-grow: 1; flex-shrink: 0; flex-basis: auto"
    >
      <p class="strong margin-bottom-md muted">{{ planFeatures.heading }}:</p>
      <ul
        class="fa-ul margin-y-0 margin-left-xl"
        style="--fa-secondary-opacity: 0.2"
      >
        <li
          v-for="(row, idx) in planFeatures.rows"
          :key="idx"
          :class="idx === planFeatures.rows.length - 1 ? '' : 'margin-bottom-sm'"
          :style="`padding-left: var(--spacing-2xs); ${row.annualOnly && isMonthlyCycle ? 'cursor: help' : ''}`"
          :aria-label="row.annualOnly && isMonthlyCycle ? 'Unlock this by switching to Annual' : null"
          :data-balloon-pos="row.annualOnly && isMonthlyCycle ? 'up' : null"
        >
          <span :style="row.annualOnly && isMonthlyCycle ? 'opacity: 0.5' : ''">
            <span
              class="fa-li"
              :style="row.annualOnly && isMonthlyCycle ? 'opacity: 0.4' : ''"
            >
              <font-awesome-icon
                :icon="row.annualOnly && isMonthlyCycle ? $v7.solid.faBan : $v7.duotone.faCircleCheck"
                size="lg"
                :flip="row.annualOnly && isMonthlyCycle ? 'horizontal' : undefined"
              />
            </span>
            {{ row.label }}
          </span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
  import accounting from 'accounting'
  import { get } from 'lodash'
  import { mapGetters, mapActions } from 'vuex'
  import {
    shouldHavePromoCode,
    getDiscountPercent,
    PLAN_COLOR_BY_SKU,
    PLAN_ICONSTYLE_BY_SKU,
    PLAN_DESCRIPTION_BY_SKU,
    PLAN_TAGLINE_BY_SKU,
    PLAN_FEATURES_BY_SKU,
    ANNUAL_SKU_FOR_MONTHLY,
    MONTHLY_SKU_FOR_ANNUAL,
    getAutoAppliedDiscounts,
    getKickstarterDiscounts,
    getPromoCodes
  } from '../PlansData.js'
  import { EventTrackingMixin } from 'app/mixins'
  import UtilsMixin from 'app/views/UtilsMixin'
  import { priceWithDiscount, doesDiscountApplyToPlan, formatTotalCurrency, discountRepeatingYears } from 'app/views/plans/priceUtils'
  import { pendingChangePlanName, formatPendingChangeDate } from 'app/views/accounts/AccountView/pendingChangeCopy'

  export default {
    name: 'PlanSummaryCard',

    mixins: [UtilsMixin, EventTrackingMixin],

    props: {
      customStyle: {
        type: Object,
        default: () => {}
      },
      modalContextSlug: {
        type: String
      },
      hasBorder: {
        type: Boolean,
        default: false
      },
      isInModalContext: {
        type: Boolean,
        default: false
      },
      isSinglePlanModal: {
        type: Boolean,
        default: false
      },
      isLast: {
        type: Boolean,
        default: true
      },
      isShowingTag: {
        type: Boolean,
        default: true
      },
      isShowingDescription: {
        type: Boolean,
        default: true
      },
      // When true, drop the "Most Popular" tag + .plan-featured lift /
      // border / shadow on this card. PlansView sets this on the Solo
      // card when the user already has a non-Solo paid plan — the
      // shopping signal is less useful then and the featured highlight
      // would compete with their "Your Plan" highlight.
      suppressFeatured: {
        type: Boolean,
        default: false
      },
      sku: {
        type: String
      },
      shortName: {
        type: String
      },
      // The plans page sets this to drive the "Billed annually / monthly"
      // label and to keep the card's UX consistent with the parent toggle.
      // The parent passes the resolved annual or monthly SKU directly via
      // `:sku`; this prop is informational for label / cycle-aware copy.
      selectedAnnual: {
        type: Boolean,
        default: true
      },
      // Bypasses `shouldSuppressDiscountUI` so URL-driven discounts surface
      // on cards that would otherwise be flagged as a downgrade for the
      // current customer. PlansView passes this when `?discount=...` is
      // explicitly in the URL — the customer arrived with a coupon in hand,
      // so showing it is the friendly thing to do.
      forceShowDiscount: {
        type: Boolean,
        default: false
      }
    },

    data() {
      return {
        currentDiscount: null,
        isShowingAboutProPlusModal: false,
        plan: null
      }
    },

    computed: {
      ...mapGetters('accounts', ['currentAccount']),
      ...mapGetters('plans', ['currentPlans', 'getPlan', 'subscriptionDiscounts']),
      ...mapGetters('subscriptions', ['currentSubscription', 'pendingChange']),
      ...mapGetters('discounts', ['globalDiscountCode']),

      periodWord() {
        return get(this.plan, ['annual'], true) ? 'year' : 'month'
      },

      buyRoute() {
        return `plans.${this.plan.slug}`
      },

      componentClass() {
        return [
          'plan-card-summary card roomy text-left display-flex flex-column padding-top-2xl with-border',
          { 'with-top-tag': this.isShowingTopTag },
          { 'margin-bottom-3xl tablet:margin-bottom-0': !this.isLast && !this.isInModalContext },
          { 'margin-bottom-3xl laptop:margin-bottom-0': !this.isLast && this.isInModalContext },
          { 'plan-on-sale': this.shouldShowSaleTag },
          { 'plan-current': this.isCurrentPlan && !this.isExpiredPlan },
          {
            'plan-featured':
              this.plan?.mostPopular && this.plan?.sku !== 'FA-STANDARD-PLUS' && !(this.isInModalContext && this.isSinglePlanModal) && !this.suppressFeatured
          },
          {
            'plan-best-value':
              this.plan?.sku === 'FA-STANDARD-PLUS' &&
              !this.shouldShowSaleTag &&
              this.shouldShowBestValueTag &&
              !this.hasActivePaidSubscription &&
              !(this.isInModalContext && this.isSinglePlanModal)
          }
        ]
      },

      // For per-tier display constants (color, description, icon-style),
      // monthly variants share the annual variant's data — they're the
      // same tier, just billed differently. Resolve the SKU we use for
      // those lookups by mapping monthly SKUs back to their annual peer.
      tierSku() {
        const sku = this.plan?.sku
        if (!sku) return null
        return ANNUAL_SKU_FOR_MONTHLY[sku] || sku
      },

      // Full SKU first so a monthly variant can describe itself, falling back
      // to the tier line for any SKU without its own entry.
      planDescriptionModal() {
        if (!this.isInModalContext) return null

        return get(PLAN_DESCRIPTION_BY_SKU, [this.plan?.sku], null) || get(PLAN_DESCRIPTION_BY_SKU, [this.tierSku], null)
      },

      // Context, not an option: neutralize the brand border `.plan-current`
      // draws, by retargeting its variable rather than adding a rule.
      isContextCard() {
        return this.isInModalContext && this.isCurrentPlan && !this.isExpiredPlan
      },

      // `.with-top-tag` reserves a top margin for a tag drawn above the card.
      // In a modal the sale tag is the only one that still renders, so that
      // margin is otherwise reserving space for nothing.
      isShowingTopTag() {
        if (this.shouldShowSaleTag) return true

        return !this.isInModalContext
      },

      // "Cloud" on the plans page; the billing cycle on a modal context card,
      // where cycle is the only axis that differs from the card beside it.
      isShowingNameTag() {
        if (this.isContextCard) return true

        return !this.plan.proDownloadEnabled && (!this.isInModalContext || this.isSinglePlanModal)
      },

      nameTagLabel() {
        if (!this.isContextCard) return 'Cloud'

        return get(this.plan, ['annual'], true) ? 'Annual' : 'Monthly'
      },

      nameTagDescription() {
        if (!this.isContextCard) return 'Served from our CDN only. No downloads.'

        return get(this.plan, ['annual'], true) ? 'Billed annually.' : 'Billed monthly. Downloads are not included.'
      },

      planTagline() {
        if (this.isInModalContext) return null
        return get(PLAN_TAGLINE_BY_SKU, [this.tierSku], null)
      },

      // Feature list for the section beneath the CTA. Always uses the ANNUAL
      // variant of the tier so capabilities like bandwidth stay consistent
      // across cycle toggles; annual-only rows dim in monthly mode.
      planFeatures() {
        if (this.isInModalContext) return null
        const tierPlan = this.tierSku ? this.getPlan(this.tierSku) : null
        if (!tierPlan) return null
        const fn = PLAN_FEATURES_BY_SKU[this.tierSku]
        return fn ? fn(tierPlan) : null
      },

      hasActivePaidSubscription() {
        const status = get(this.currentSubscription, ['status'], null)
        const type = get(this.currentSubscription, ['type'], null)

        return status === 'active' && type !== 'free'
      },

      // Stripe customers the native plan-change flow serves: active, or
      // past_due (a failed renewal they can settle by changing plan).
      isStripeChangeFlowCustomer() {
        const status = get(this.currentSubscription, ['status'], null)
        const type = get(this.currentSubscription, ['type'], null)

        return type === 'stripe' && (status === 'active' || status === 'past_due')
      },

      discountPercent() {
        return getDiscountPercent(this.currentAccount, this.currentSubscription)
      },

      componentStyle() {
        let style = {}

        style['--card-roomy-vertical-padding'] = 'var(--spacing-2xl)'
        style['--card-roomy-horizontal-padding'] = 'var(--spacing-2xl)'
        style['--card-margin-bottom'] = '0'
        style['flex'] = '1'
        // Used by .plan-current border + the "Your Plan" tag so the
        // current-plan treatment matches each plan's brand color. In a modal
        // the current plan is context rather than an option, so it drops to
        // grey and leaves the brand color to the card being offered.
        style['--current-plan-color'] = this.isContextCard ? 'var(--fa-lt-gravy)' : `var(--fa-${this.planColor})`

        if (this.isInModalContext) {
          style['--border-width'] = 'var(--border-width-sm)'
        }

        return {
          ...style,
          ...this.customStyle
        }
      },

      icon() {
        return get(this.plan, ['iconName'], 'faMoon')
      },

      planColor() {
        return get(PLAN_COLOR_BY_SKU, [this.tierSku], 'yellow')
      },

      planIconStyle() {
        return get(PLAN_ICONSTYLE_BY_SKU, [this.tierSku], 'solid')
      },

      displayPlanPriceAmount() {
        return get(this.plan, ['price', 'amount'], 0)
      },

      isDowngrade() {
        const currentPlan = get(this.currentSubscription, ['plan'], null)
        const currentSubscriptionStatus = get(this.currentSubscription, ['status'], null)
        const currentPlanSku = get(currentPlan, ['sku'])

        if (currentSubscriptionStatus === 'canceled') return false

        // Free sits below every paid tier — moving from Free to any
        // paid plan is always an upgrade, never a downgrade. Without
        // this guard, FA-FREE's `allowedUpgrades` seed list (which
        // doesn't enumerate the new tier SKUs) would mis-classify every
        // card as a downgrade for logged-in Free users.
        if (currentPlanSku === 'FA-FREE') return false

        return currentPlan ? !get(currentPlan, ['allowedUpgrades'], []).includes(get(this.plan, ['sku'], 'NOPLAN')) : false
      },

      isCurrentPlan() {
        const currentPlanSku = get(this.currentSubscription, ['plan', 'sku'], null)
        return this.plan.sku === currentPlanSku
      },

      isExpiredPlan() {
        const currentPlanSku = get(this.currentSubscription, ['plan', 'sku'], null)
        const subStatusCancelled = get(this.$subscription, ['status']) === 'canceled'
        return this.plan.sku === currentPlanSku && subStatusCancelled
      },

      isExpiredActivePlan() {
        const currentPlanSku = get(this.currentSubscription, ['plan', 'sku'], null)
        const subStatusCancelled = get(this.$subscription, ['status']) === 'canceled'
        return subStatusCancelled && this.plan?.sku === currentPlanSku
      },

      monthlyEquivalentAmount() {
        return this.isMonthlyCycle ? this.displayPlanPriceAmount : Math.round(this.displayPlanPriceAmount / 12)
      },

      // Apply the discount to the full cycle amount FIRST, then derive the
      // monthly-equivalent for display. Flat-dollar coupons are sized for
      // the cycle they're applied to — taking $10 off $96/yr is correct;
      // taking $10 off the $8/mo display number isn't (and would underflow
      // for many real coupons). priceWithDiscount clamps at 0 so the result
      // is always renderable.
      fullCycleDiscountedAmount() {
        if (this.isShowingDiscount && this.currentDiscount) {
          return priceWithDiscount(this.plan, this.displayPlanPriceAmount, this.currentDiscount)
        }
        return this.displayPlanPriceAmount
      },

      currentPrice() {
        const price = this.isMonthlyCycle ? this.fullCycleDiscountedAmount : Math.round(this.fullCycleDiscountedAmount / 12)
        return formatTotalCurrency(price / 100)
      },

      originalMonthlyPrice() {
        return formatTotalCurrency(this.monthlyEquivalentAmount / 100)
      },

      originalMonthlyDollars() {
        return this.originalMonthlyPrice[0]
      },

      originalMonthlyCents() {
        return this.originalMonthlyPrice[1]
      },

      // Structural cycle savings on annual cards: paying-monthly-for-a-
      // year minus FULL-PRICE annual. Discount-independent — promotional
      // savings are surfaced separately via `discountParts` so the two
      // values stay decoupled and the dollar amounts on this card never
      // collide with what the buy page applies as the coupon discount.
      annualSavingsAmount() {
        if (this.isMonthlyCycle) return null
        if (this.monthlyVariantAnnualizedAmount === null) return null
        const savings = this.monthlyVariantAnnualizedAmount - this.displayPlanPriceAmount
        return savings > 0 ? savings : null
      },

      annualSavingsFormatted() {
        if (this.annualSavingsAmount === null) return null
        return accounting.formatMoney(this.annualSavingsAmount / 100, null, 0)
      },

      annualSavingsLine() {
        if (this.annualSavingsFormatted === null) return null
        return `${this.annualSavingsFormatted} annual savings`
      },

      // Plan-aware "extra dark" color for the savings + discount text.
      // `--fa-dk-yellow` (yellow6) is borderline on light backgrounds at
      // body size, so yellow gets two steps darker; gravy + teal step
      // one notch deeper for consistency. Falls back to the standard
      // dk-* token for any unmapped plan color.
      savingsTextColor() {
        const map = {
          gravy: 'var(--gray-700)',
          yellow: 'var(--yellow8)',
          teal: 'var(--teal8)'
        }
        return map[this.planColor] || `var(--fa-dk-${this.planColor})`
      },

      // Promotional-discount line shared by both cycles ("50% off first
      // year!", "$20 off for 3 months!", "50% off forever!"). The unit
      // (year vs month) follows the card's cycle; the magnitude follows
      // the coupon shape (percent preferred, dollar fallback). The full
      // line renders in dk-blue. The exact dollar amount lives on the
      // buy page line item; the card just teases the offer. Returns null
      // when no discount applies or the discount data is incomplete.
      discountLine() {
        if (!this.isShowingDiscount || !this.currentDiscount) return null
        if (this.displayPlanPriceAmount - this.fullCycleDiscountedAmount <= 0) return null

        const percentOff = get(this.currentDiscount, 'percentOff', null)
        const amountOff = get(this.currentDiscount, 'amountOff', null)
        let magnitude
        if (percentOff !== null && percentOff !== undefined) {
          magnitude = `${percentOff}% off`
        } else if (amountOff !== null && amountOff !== undefined) {
          magnitude = `${accounting.formatMoney(amountOff / 100, null, 0)} off`
        } else {
          return null
        }

        const unit = this.isMonthlyCycle ? 'month' : 'year'
        const count = this.isMonthlyCycle
          ? get(this.currentDiscount, 'durationInMonths', 1)
          : discountRepeatingYears(this.currentDiscount)

        switch (this.discountDuration) {
          case 'once':
            return `${magnitude} first ${unit}!`
          case 'repeating':
            return count > 1
              ? `${magnitude} for ${count} ${unit}s!`
              : `${magnitude} first ${unit}!`
          case 'forever':
            return `${magnitude} forever!`
          default:
            return null
        }
      },


      // Annualized cost if this tier were billed monthly (cents). Drives
      // both the strike-through "what monthly would cost yearly" framing
      // and the "Save $X per year" savings sentence on annual cards.
      monthlyVariantAnnualizedAmount() {
        let monthlyAmount
        if (this.isMonthlyCycle) {
          monthlyAmount = this.displayPlanPriceAmount
        } else {
          const monthlySku = MONTHLY_SKU_FOR_ANNUAL[this.plan?.sku]
          const monthlyPlan = monthlySku ? this.getPlan(monthlySku) : null
          monthlyAmount = get(monthlyPlan, ['price', 'amount'], null)
        }
        if (monthlyAmount === null || monthlyAmount === undefined) return null
        return monthlyAmount * 12
      },

      monthlyVariantAnnualizedFormatted() {
        if (this.monthlyVariantAnnualizedAmount === null) return null
        return accounting.formatMoney(this.monthlyVariantAnnualizedAmount / 100, null, 0)
      },

      shouldSuppressDiscountUI() {
        // `forceShowDiscount` overrides the downgrade suppression so a
        // URL-driven discount stays visible regardless of how the card
        // would normally be classified for the current customer.
        if (this.forceShowDiscount) return false
        return this.isDowngrade || (this.isCurrentPlan && !this.isExpiredPlan)
      },

      isShowingDiscount() {
        if (this.shouldSuppressDiscountUI) return false
        if (!this.currentDiscount) return false

        return doesDiscountApplyToPlan(this.plan, this.currentDiscount)
      },

      planHasProPlusIcons() {
        return this.plan?.iconCollection?.name === 'pro-plus-collection'
      },

      // 018-native-plan-changes / FR-021: paid Stripe customers currently
      // on a Pro+ plan (Solo or Team — anything backed by the
      // pro-plus-collection) can't downgrade to a plan that loses that
      // access. The plan-change API enforces this with a hard 409; we
      // surface it at the source so the customer doesn't have to navigate
      // into the change view to find out their selection isn't allowed.
      wouldLoseProPlusAccess() {
        const currentHasProPlus =
          get(this.currentSubscription, ['plan', 'iconCollection', 'name']) === 'pro-plus-collection'

        return (
          this.hasActivePaidSubscription &&
          get(this.currentSubscription, ['type']) === 'stripe' &&
          currentHasProPlus &&
          !this.planHasProPlusIcons
        )
      },

      proPlusBlockTooltip() {
        return `Switching to ${this.plan?.name || 'this plan'} would remove your Pro+ icon access. Contact us to discuss options.`
      },

      proPlusContactMailto() {
        const planName = this.plan?.name || 'a new plan'
        const subject = encodeURIComponent(`I want to switch to ${planName} but it would remove Pro+ access`)
        return `mailto:hello@fontawesome.com?subject=${subject}`
      },

      // Annual → monthly transitions are temporarily non-self-serve
      // (downstream cycle-switch work hasn't shipped). Mirror the Pro+
      // gate's "Contact us" pattern so an annual customer toggling the
      // /plans cycle slider to monthly sees a contact path rather than
      // routing into a flow that the backend will reject anyway. Legacy
      // customers are universally annual, so this catches them too.
      wouldRequireAnnualToMonthlySwitch() {
        if (!this.hasActivePaidSubscription) return false
        if (this.isCurrentPlan) return false
        if (get(this.currentSubscription, ['type']) !== 'stripe') return false
        const currentIsAnnual = get(this.currentSubscription, ['plan', 'annual'], true) === true
        const targetIsMonthly = get(this.plan, ['annual'], true) === false
        return currentIsAnnual && targetIsMonthly
      },

      annualToMonthlyBlockTooltip() {
        return `Switching from an annual plan to a monthly plan isn't supported yet. Contact us to discuss options.`
      },

      annualToMonthlyContactMailto() {
        const planName = this.plan?.name || 'a monthly plan'
        const subject = encodeURIComponent(`I want to switch from my annual plan to ${planName}`)
        return `mailto:hello@fontawesome.com?subject=${subject}`
      },

      // True when the customer already scheduled a change to this card's
      // plan. The /plans page only renders new-tier cards (Starter, Solo,
      // Team — annual and monthly), so a simple SKU match is enough.
      hasPendingChangeToThisPlan() {
        const targetSku = get(this.pendingChange, ['targetPlan'])
        if (!targetSku) return false
        return targetSku === this.plan?.sku
      },

      pendingChangeScheduledTooltip() {
        if (!this.pendingChange) return ''
        const planName = pendingChangePlanName(this.pendingChange.targetPlan)
        const date = formatPendingChangeDate(this.pendingChange.effectiveAt)
        return `Your plan changes to ${planName} on ${date}. Manage from your account.`
      },

      isSameTierCycleSwap() {
        if (!this.hasActivePaidSubscription) return false
        if (this.isCurrentPlan) return false
        const subSku = get(this.currentSubscription, ['plan', 'sku'])
        const cardSku = this.plan?.sku
        if (!subSku || !cardSku) return false
        return MONTHLY_SKU_FOR_ANNUAL[subSku] === cardSku
          || ANNUAL_SKU_FOR_MONTHLY[subSku] === cardSku
      },

      planCycleWord() {
        return this.plan?.annual === false ? 'Monthly' : 'Annual'
      },

      planHasProDownload() {
        return this.plan?.proDownloadEnabled
      },

      planIsOnlineOnly() {
        return !this.plan?.proDownloadEnabled
      },

      discountDuration() {
        return get(this.currentDiscount, 'duration', null)
      },

      planPrice() {
        return accounting.formatMoney(this.displayPlanPriceAmount / 100, null, 0)
      },

      currentDollars() {
        return this.currentPrice[0]
      },

      currentCents() {
        return this.currentPrice[1]
      },

      isMonthlyCycle() {
        return this.plan?.annual === false
      },


      shouldShowSaleTag() {
        if (this.shouldSuppressDiscountUI) return false

        return this.currentDiscount ? doesDiscountApplyToPlan(this.plan, this.currentDiscount) : false
      }
    },

    async created() {
      const data = []
      if (!this.plansLoaded) {
        data.push(this.fetchPlans())
      }
      this.plan = this.planBySku(this.sku)

      const plansToCheck = [this.plan]

      const setCurrentDiscount = () => {
        if (this.subscriptionDiscounts.length > 1) {
          this.currentDiscount =
            this.subscriptionDiscounts.find((discount) => plansToCheck.some((p) => get(discount, ['appliesTo', 'products'], []).includes(p.sku))) || null
        } else if (this.subscriptionDiscounts.length === 1 && plansToCheck.some((p) => doesDiscountApplyToPlan(p, this.subscriptionDiscounts[0]))) {
          this.currentDiscount = this.subscriptionDiscounts[0]
        }
      }

      setCurrentDiscount()

      if (shouldHavePromoCode(this.currentAccount, this.currentSubscription)) {
        // Account-specific, so no parent resolves it on this card's behalf.
        data.push(this.fetchDiscounts([getPromoCodes()[this.discountPercent]['id']]))
        this.promoCode = getPromoCodes()[this.discountPercent]['code']
      } else if (!this.isInModalContext) {
        // UpsellOptions fetches and awaits these before mounting any card, so
        // in a modal each card would only re-request what the store already
        // holds — once per card.
        if (this.globalDiscountCode) {
          data.push(this.fetchDiscounts([this.globalDiscountCode]))
        } else if (getKickstarterDiscounts().length > 0 && this.isBackerWithoutActiveSubscription) {
          data.push(this.fetchDiscounts(getKickstarterDiscounts()))
        } else if (getAutoAppliedDiscounts().length > 0) {
          data.push(this.fetchDiscounts(getAutoAppliedDiscounts()))
        }
      }

      try {
        await Promise.all(data)
        setCurrentDiscount()
      } finally {
        this.isLoading = false
      }
    },

    methods: {
      ...mapActions('plans', ['fetchPlans', 'fetchDiscounts']),

      featureListItemClass(feature_slug) {
        return this.modalContextSlug.includes(feature_slug) ? 'b' : null
      },

      // The card renders in exactly two places: the plans page and the upsell
      // modal. Only the modal branch used to report, so the primary CTA on the
      // plans page — the last click before checkout — emitted nothing.
      trackThen(event, isInModalContext, targetPlan) {
        if (isInModalContext) {
          this.purchaseFlowEvent('get_pro_button_click', {
            context: 'upsell_modal',
            feature: this.modalContextSlug
          })
        } else {
          this.purchaseFlowEvent('get_pro_button_click', {
            context: 'plans_view',
            plan_sku: targetPlan?.sku,
            cycle: this.selectedAnnual ? 'annual' : 'monthly'
          })
        }
      },

      planBySku(sku) {
        const plan = this.currentPlans.find((plan) => plan.sku == sku)

        if (!plan) {
          throw new Error(`Cannot find a plan with SKU "${sku}" in currentPlans.`)
        }

        return plan
      },

      planLinkArguments(plan) {
        // T066 (018-native-plan-changes): paid Stripe customers route to the
        // native plan-change flow, not the per-plan PlanBuyView (which would
        // dead-end on the IsUpgrading "go to Stripe portal" redirect).
        // Free / Braintree / PayPal customers continue through PlanBuyView.
        //
        // We pass the chosen plan's slug as a route param so PlanChangeView
        // seeds its picker to that target (e.g., "team-monthly") instead of
        // defaulting to the customer's current plan — mirroring how the buy
        // flow's `/plans/:slug` carries the selection into PlanBuyView.
        //
        // A past_due Stripe customer belongs in the change flow too: Checkout
        // would 409 them with change_plan_required and bounce them there after
        // they'd already filled in the payment form.
        if (this.isStripeChangeFlowCustomer) {
          return { name: 'plans.change', params: { slug: plan.slug } }
        }

        if (this.currentDiscount) {
          if (doesDiscountApplyToPlan(plan, this.currentDiscount)) {
            return { name: this.buyRoute, query: { discount: this.currentDiscount.promoCode } }
          }
        }

        return { name: this.buyRoute }
      }
    }
  }
</script>
